import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { formatApiError } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Bot, Loader2, Plus, Trash2, TrendingUp } from "lucide-react";

const FUNNEL_LABEL = {
  pre_outlook: "Pre-Outlook", lead: "Parcheggio Lead", progetto: "Progetto", parcheggio: "Parcheggio",
};
const STATUS_LABEL = {
  active: "Attivo", paused: "In pausa", completed: "Completato",
  parking: "Parcheggio", cemetery: "Cimitero", reactivated: "Riattivato",
};

export default function AutomationLeadPanel({ leadId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/automations/leads/${leadId}/panel`);
      setData(data);
    } catch (e) {
      // pannello non bloccante: se l'utente non ha i permessi, non mostrare nulla
      setData({ error: true });
    } finally { setLoading(false); }
  }, [leadId]);

  useEffect(() => { load(); }, [load]);

  const enroll = async () => {
    if (!sel) { toast.error("Seleziona un funnel"); return; }
    setBusy(true);
    try {
      await api.post(`/automations/leads/${leadId}/enroll`, { workflow_id: sel });
      toast.success("Lead iscritto al funnel");
      setSel("");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const unenroll = async (eid) => {
    setBusy(true);
    try {
      await api.delete(`/automations/enrollments/${eid}`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  if (loading) return null;
  if (!data || data.error) return null;

  const activeEnrolls = (data.enrollments || []).filter((e) => ["active", "paused"].includes(e.status));

  return (
    <div className="mt-5 pt-5 border-t border-border" data-testid="automation-lead-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs uppercase tracking-widest text-muted-foreground flex items-center gap-1.5">
          <Bot className="h-3.5 w-3.5 text-amber-500" /> Automazioni
        </div>
        <div className="inline-flex items-center gap-1 text-sm font-heading font-bold" title="Punteggio di interesse (scoring dinamico)" data-testid="automation-score">
          <TrendingUp className="h-3.5 w-3.5 text-amber-500" /> {Number(data.score || 0).toFixed(0)} pt
        </div>
      </div>

      {!data.master_enabled && (
        <p className="text-[10px] text-amber-600 mb-2" data-testid="automation-master-off">
          Interruttore automazioni spento: gli invii partiranno solo quando l'admin lo attiva.
        </p>
      )}

      {activeEnrolls.length > 0 && (
        <div className="space-y-1.5 mb-3">
          {activeEnrolls.map((e) => (
            <div key={e.id} className="flex items-center justify-between text-xs rounded border border-border px-2 py-1.5" data-testid={`enroll-row-${e.id}`}>
              <span>
                <span className="font-medium">{FUNNEL_LABEL[e.funnel_type] || e.funnel_type}</span>
                <span className="text-muted-foreground"> · {STATUS_LABEL[e.status] || e.status}</span>
              </span>
              <button onClick={() => unenroll(e.id)} disabled={busy} className="text-red-500 hover:text-red-600" data-testid={`enroll-remove-${e.id}`} title="Rimuovi dal funnel">
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {(data.workflows || []).length === 0 ? (
        <p className="text-[11px] text-muted-foreground">Nessun funnel attivo disponibile. Abilita un workflow in Automazioni CRM.</p>
      ) : (
        <div className="flex items-center gap-2">
          <Select value={sel} onValueChange={setSel}>
            <SelectTrigger className="h-9 text-sm" data-testid="automation-funnel-select"><SelectValue placeholder="Iscrivi a un funnel" /></SelectTrigger>
            <SelectContent>
              {data.workflows.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button size="sm" onClick={enroll} disabled={busy || !sel} data-testid="automation-enroll-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
          </Button>
        </div>
      )}
    </div>
  );
}
